import { useIntl } from 'react-intl'
import { KTIcon } from '../../_metronic/helpers'
import { EFileBadgeStatus, EFileStatus } from '../../types/enums'
import { useCallback, useState } from 'react'
import { openFile } from '../../utils/openFile'
import { SmartTooltip } from '../SmartTooltip/SmartTooltip'
import styles from './FileStatusBadge.module.css'

const fileIconColor: Record<EFileStatus, string> = {
  [EFileStatus.PROCESSING]: 'text-primary',
  [EFileStatus.REJECTED]: 'text-danger',
  [EFileStatus.VERIFIED]: 'text-success',
}

const fileBadgeColor: Record<EFileBadgeStatus, string> = {
  [EFileBadgeStatus.PROCESSING]: 'badge-light-primary',
  [EFileBadgeStatus.PENDING]: 'badge-light-primary',
  [EFileBadgeStatus.APPROVED]: 'badge-light-success',
  [EFileBadgeStatus.REJECTED]: 'badge-light-danger',
  [EFileBadgeStatus.NEEDS_REVIEW]: 'badge-light-info',
  [EFileBadgeStatus.ERROR]: 'badge-light-warning',
  // Проверка намеренно не проводилась — нейтральный бейдж, а не цветной:
  // это не исход проверки. text-success тут стоял по недосмотру и был недостижим,
  // пока бэкенд не начал выставлять disabled.
  [EFileBadgeStatus.DISABLED]: styles.neutral,
}

type BadgePosition = 'start' | 'end' | 'hidden'

interface IFileDownloadButtonProps {
  url: string
  id?: number | string
  className?: string
  hasColorCode?: boolean
  status?: EFileStatus | undefined
  badgeStatus?: EFileBadgeStatus | null
  badgekMessage?: string | null
  badgePosition?: BadgePosition
}

const FileStatusBadge = ({
  status,
  message,
  position = 'start',
}: {
  status?: EFileBadgeStatus | null
  message?: string | null
  position?: BadgePosition
}) => {
  const text = message?.trim() ?? ''
  const disabled = !text

  if (position === 'hidden') return null

  return (
    <span
      style={{
        display: 'inline-flex',
        minWidth: '90px',
        justifyContent: 'center',
        order: position === 'end' ? 2 : 0,
      }}
    >
      {status != null && (<>
        <SmartTooltip content={text} disabled={disabled}>
          <span 
            className={`badge ${fileBadgeColor[status] ?? ''}`} 
            style={{ cursor: disabled ? 'default' : 'help', userSelect: 'none' }}
            tabIndex={disabled ? -1 : 0}
          >
            {status}
          </span>
        </SmartTooltip>
      </>)}
    </span>
  )
}

export const FileDownloadButton = ({
  url,
  id,
  className,
  hasColorCode = true,
  status,
  badgeStatus,
  badgekMessage,
  badgePosition = 'start',
}: IFileDownloadButtonProps ) => {
  const intl = useIntl()

  const handleFileClick = useCallback(async () => {
    if (!url) return
    openFile(url)
  }, [url])

  return (   
    <div className='d-flex align-items-center'>
      <FileStatusBadge status={badgeStatus} message={badgekMessage} position={badgePosition} />
      <button
        className={`btn btn-icon btn-bg-light btn-sm ${className || ''}`}
        onClick={handleFileClick}
        style={{ cursor: 'pointer' }}
        title={`${intl.formatMessage({ id: 'receipt.action.VIEW' })}${id ? `: ${id.toString()}` : `` }`}
      >
        <KTIcon
          className={`fs-1 ${hasColorCode && status ? fileIconColor?.[status] : 'text-muted'}`}
          iconName='file'
        />
      </button>    
    </div>
  )
}
